import { CheckCircleOutlined, StopOutlined, SyncOutlined } from '@ant-design/icons';
import useAuth from '@hooks/use-auth';
import type { Conversation } from '@interfaces/conversation';
import { Role } from '@interfaces/role';
import type { TelegramGroup } from '@interfaces/telegram-group';
import type { TelegramUser } from '@interfaces/telegram-user';
import { apiRequest, isResponse } from '@services/fetcher';
import buildClassName from '@utils/build-class-name';
import { Button, message, Tooltip } from 'antd';
import type { FC } from 'react';
import { memo, useCallback, useState } from 'react';

import { useBoundStore } from '@/newstore';
import { useActiveConversation, useActiveConversationMembers } from '@/newstore/selectors/shared';

import styles from './RightColumn.module.scss';

interface StateProps {
  conversation: Conversation | null;
  conversationUsers: TelegramUser[];
  isSyncingName: boolean;
  isTogglingAuthorization: boolean;
  onSyncName: () => void;
  onToggleAuthorization: () => void;
}

const NonMemoRightColumn: FC<StateProps> = ({
  conversation,
  conversationUsers,
  isSyncingName,
  isTogglingAuthorization,
  onSyncName,
  onToggleAuthorization,
}) => {
  const auth = useAuth();
  const isAdmin = auth.hasRole(Role.Admin);
  const wrapperClassName = buildClassName(styles.wrapper, !conversation && styles.isHidden);

  return (
    <div id="right-column-wrapper" className={wrapperClassName}>
      <aside id="right-column" className={styles.root}>
        {conversation && (
          <section className={styles.railSection}>
            <h2 className={styles.railTitle}>Conversation</h2>
            <div className={styles.railMeta}>
              <span>Language</span>
              <strong>{conversation.language_code.toUpperCase()}</strong>
            </div>
            <div className={styles.railMeta}>
              <span>Status</span>
              <strong>{conversation.is_authorized ? 'Authorized' : 'Not authorized'}</strong>
            </div>
          </section>
        )}
        <section className={buildClassName(styles.railSection, styles.participantsSection)}>
          <h2 className={styles.railTitle}>Participants ({conversationUsers.length})</h2>
          {conversationUsers.length === 0 ? (
            <p className={styles.emptyHint}>No participant details available.</p>
          ) : (
            <ul className={styles.participantList}>
              {conversationUsers.map((user) => (
                <li key={user.id} className={styles.participantItem}>
                  <span>{user.full_name}</span>
                  {user.phone_number && <a href={`tel:${user.phone_number}`}>{user.phone_number}</a>}
                </li>
              ))}
            </ul>
          )}
        </section>
        {isAdmin && conversation && (
          <section className={styles.railSection}>
            <h2 className={styles.railTitle}>Actions</h2>
            <div className={styles.actionButtons}>
              <Tooltip title="Update conversation name from Telegram">
                <Button
                  title="Update conversation name from Telegram"
                  className={styles.actionButton}
                  icon={<SyncOutlined spin={isSyncingName} />}
                  disabled={!conversation.is_authorized || isSyncingName}
                  onClick={onSyncName}
                />
              </Tooltip>
              <Tooltip title={conversation.is_authorized ? 'Revoke authorization' : 'Authorize conversation'}>
                <Button
                  title={conversation.is_authorized ? 'Revoke authorization' : 'Authorize conversation'}
                  className={styles.actionButton}
                  icon={conversation.is_authorized ? <StopOutlined /> : <CheckCircleOutlined />}
                  loading={isTogglingAuthorization}
                  onClick={onToggleAuthorization}
                />
              </Tooltip>
            </div>
          </section>
        )}
      </aside>
    </div>
  );
};

export default memo(function RightColumn() {
  const auth = useAuth();
  const { conversation } = useActiveConversation();
  const conversationUsers = useActiveConversationMembers();
  const [isSyncingName, setSyncingName] = useState(false);
  const [isTogglingAuthorization, setTogglingAuthorization] = useState(false);
  const [messageApi, messagesContextHolder] = message.useMessage();

  const patchCurrentConversation = useCallback(
    (patch: Partial<Conversation>) => {
      const conversationId = conversation?.id;
      if (!conversationId) {
        return;
      }

      useBoundStore.getState().conversations.byId[conversationId]?.patchConversation(patch);
    },
    [conversation?.id],
  );

  const handleSyncName = useCallback(async () => {
    if (!conversation || isSyncingName || !conversation.is_authorized) {
      return;
    }

    setSyncingName(true);

    try {
      const response = await apiRequest<TelegramGroup>(
        `${import.meta.env.VITE_API_URL}/manage/telegram-group/${conversation.id}/sync`,
        auth,
        {
          method: 'POST',
        },
      );
      setSyncingName(false);

      if (isResponse(response)) {
        messageApi.error(response.statusText || 'Failed to sync conversation name.');
        return;
      }
      if (response instanceof Error) {
        messageApi.error(response.message || 'Failed to sync conversation name.');
        return;
      }

      patchCurrentConversation({
        name: response.name,
        language_code: response.language_code,
        is_authorized: response.is_authorized,
      });
      messageApi.success('Conversation name synced.');
    } catch {
      setSyncingName(false);
      messageApi.error('Problem encountered syncing name.');
    }
  }, [auth, conversation, isSyncingName, messageApi, patchCurrentConversation]);

  const handleToggleAuthorization = useCallback(async () => {
    if (!conversation) {
      return;
    }

    if (isTogglingAuthorization) {
      return;
    }

    setTogglingAuthorization(true);

    try {
      const response = await apiRequest<TelegramGroup>(
        `${import.meta.env.VITE_API_URL}/manage/telegram-group/${conversation.id}`,
        auth,
        {
          method: 'PATCH',
          body: JSON.stringify({
            name: conversation.name,
            language_code: conversation.language_code,
            is_authorized: !conversation.is_authorized,
          }),
        },
      );
      setTogglingAuthorization(false);

      if (isResponse(response)) {
        messageApi.error(response.statusText || 'Failed to update authorization state.');
        return;
      }
      if (response instanceof Error) {
        messageApi.error(response.message || 'Failed to update authorization state.');
        return;
      }

      patchCurrentConversation({
        name: response.name,
        language_code: response.language_code,
        is_authorized: response.is_authorized,
      });
      messageApi.success(response.is_authorized ? 'Conversation authorized.' : 'Conversation deauthorized.');
    } catch {
      setTogglingAuthorization(false);
      messageApi.error('Problem encountered authorizing/deauthorizing conversation.');
    }
  }, [auth, conversation, isTogglingAuthorization, messageApi, patchCurrentConversation]);

  return (
    <>
      <NonMemoRightColumn
        conversation={conversation}
        conversationUsers={conversationUsers ?? []}
        isSyncingName={isSyncingName}
        isTogglingAuthorization={isTogglingAuthorization}
        onSyncName={handleSyncName}
        onToggleAuthorization={handleToggleAuthorization}
      />
      {messagesContextHolder}
    </>
  );
});
